// src/components/VeiculoDetalhesModal.js

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import './VeiculoDetalhesModal.css';

import {
    FaTimes, FaDollarSign, FaChartLine, FaCheckCircle,
    FaTools, FaListAlt, FaCarSide, FaSpinner, FaExclamationCircle, FaWrench,
    FaChartPie, FaReceipt, FaGasPump, FaMobileAlt
} from 'react-icons/fa';

const formatCurrency = (value) => (parseFloat(value || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Status da OS → cor/rótulo do badge
const OS_STATUS_META = {
    'Concluído':           { label: 'Concluído',       cls: 'green'  },
    'Finalizada':          { label: 'Concluído (App)', cls: 'green'  },
    'Em Andamento':        { label: 'Em Andamento',    cls: 'blue'   },
    'Na Fila':             { label: 'Na Fila',         cls: 'indigo' },
    'Agendado':            { label: 'Agendado',        cls: 'amber'  },
    'Cancelado':           { label: 'Cancelado',       cls: 'red'    },
    'Lançamento Excluído': { label: 'Excluído',        cls: 'gray'   },
};

// Origem do gasto → ícone/cor da tag
const ORIGEM_META = {
    manutencao:  { Icon: FaWrench,    label: 'Manutenção',  cls: 'indigo' },
    app:         { Icon: FaMobileAlt, label: 'App',         cls: 'blue'   },
    combustivel: { Icon: FaGasPump,   label: 'Combustível', cls: 'orange' },
};

export default function VeiculoDetalhesModal({ veiculo, onClose }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/vehicles/${veiculo.id}/details`);
            setData(response.data);
        } catch (err) {
            setError('Não foi possível carregar os detalhes analíticos do veículo.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [veiculo.id]);

    useEffect(() => {
        if (veiculo?.id) fetchDetails();
    }, [veiculo?.id, fetchDetails]);

    const renderDate = (dateString) => {
        if (!dateString) return 'N/D';
        try { return format(new Date(dateString), 'dd/MM/yy', { locale: ptBR }); }
        catch { return 'Inválido'; }
    };

    const renderContent = () => {
        if (isLoading && !data) return (
            <div className="vec-dm-state-box">
                <FaSpinner className="vec-dm-icon-spin" />
                <p>Calculando rentabilidade do veículo...</p>
            </div>
        );

        if (error) return (
            <div className="vec-dm-state-box error">
                <FaExclamationCircle className="vec-dm-icon-error" />
                <p>{error}</p>
            </div>
        );

        if (!data) return null;

        const { dados_cadastrais, insights, historico_os, historico_manutencao } = data;

        const faturamentoTotal = parseFloat(insights.faturamento_total || 0);
        const custoOperacional = parseFloat(insights.custo_operacional_total || 0);
        const lucroTotal = parseFloat(insights.lucro_total || 0);
        const balancoColor = lucroTotal >= 0 ? "vec-dm-text-green" : "vec-dm-text-red";

        // ── Métricas derivadas (painel de Rentabilidade) ──────────────────────
        const totalServicos = Number(insights.total_servicos || 0);
        const ticketMedio    = totalServicos > 0 ? faturamentoTotal / totalServicos : 0;
        const margem         = faturamentoTotal > 0 ? (lucroTotal / faturamentoTotal) * 100 : 0;

        // Normaliza as barras pelo maior valor entre faturamento e custo,
        // garantindo um mínimo visível quando há valor > 0.
        const base    = Math.max(faturamentoTotal, custoOperacional, 1);
        const wFat     = faturamentoTotal > 0 ? Math.max(3, (faturamentoTotal / base) * 100) : 0;
        const wCusto   = custoOperacional > 0 ? Math.max(3, (custoOperacional / base) * 100) : 0;

        const margemCls = margem >= 50 ? 'green' : margem >= 0 ? 'amber' : 'red';
        const margemLabel = faturamentoTotal > 0 ? `${margem.toFixed(0)}%` : '—';

        // ── Status do veículo (chip no header) ────────────────────────────────
        const statusVeiculo = String(dados_cadastrais.status || '').toLowerCase();
        const chipCls =
            statusVeiculo.includes('servi')   ? 'servico'    :
            statusVeiculo.includes('manuten') ? 'manutencao' :
            statusVeiculo.includes('dispon')  ? 'disponivel' : 'outro';

        return (
            <>
                <header className="vec-dm-header">
                    <div className="vec-dm-profile">
                        <div className="vec-dm-avatar">
                            <FaCarSide />
                        </div>
                        <div className="vec-dm-header-titles">
                            <h2>{dados_cadastrais.marca} {dados_cadastrais.modelo}</h2>
                            <div className="vec-dm-header-meta">
                                <span className="vec-dm-doc">
                                    Placa: {dados_cadastrais.placa} | Ano: {dados_cadastrais.ano}
                                </span>
                                {dados_cadastrais.status && (
                                    <span className={`vec-dm-status-chip ${chipCls}`}>
                                        {dados_cadastrais.status}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="vec-dm-close" title="Fechar (Esc)"><FaTimes /></button>
                </header>

                <div className="vec-dm-body">

                    {/* --- LINHA 1: INSIGHTS FINANCEIROS (KPIs) --- */}
                    <div className="vec-dm-grid-4">
                        <div className="vec-dm-card">
                            <div className="vec-dm-icon blue"><FaCheckCircle /></div>
                            <div className="vec-dm-info">
                                <label>Total de Serviços</label>
                                <strong>{totalServicos} OS</strong>
                            </div>
                        </div>
                        <div className="vec-dm-card">
                            <div className="vec-dm-icon green"><FaDollarSign /></div>
                            <div className="vec-dm-info">
                                <label>Faturamento Gerado</label>
                                <strong>{formatCurrency(faturamentoTotal)}</strong>
                            </div>
                        </div>
                        <div className="vec-dm-card">
                            <div className="vec-dm-icon orange"><FaWrench /></div>
                            <div className="vec-dm-info">
                                <label>Custo Manutenções</label>
                                <strong className="vec-dm-text-orange">{formatCurrency(custoOperacional)}</strong>
                            </div>
                        </div>
                        <div className="vec-dm-card">
                            <div className={`vec-dm-icon ${lucroTotal >= 0 ? 'green' : 'red'}`}><FaChartLine /></div>
                            <div className="vec-dm-info">
                                <label>Lucro Líquido</label>
                                <strong className={balancoColor}>{formatCurrency(lucroTotal)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* --- NOVO: PAINEL DE RENTABILIDADE --- */}
                    <div className="vec-dm-profit-panel">
                        <div className="vec-dm-profit-head">
                            <div className="vec-dm-profit-title">
                                <FaChartPie /> Rentabilidade do Veículo
                            </div>
                            <span className={`vec-dm-margin-pill ${margemCls}`}>
                                Margem de lucro {margemLabel}
                            </span>
                        </div>

                        <div className="vec-dm-bar-row">
                            <span className="vec-dm-bar-label">
                                <FaDollarSign /> Faturamento
                            </span>
                            <div className="vec-dm-bar-track">
                                <div className="vec-dm-bar-fill green" style={{ width: `${wFat}%` }} />
                            </div>
                            <span className="vec-dm-bar-value">{formatCurrency(faturamentoTotal)}</span>
                        </div>

                        <div className="vec-dm-bar-row">
                            <span className="vec-dm-bar-label">
                                <FaWrench /> Custo
                            </span>
                            <div className="vec-dm-bar-track">
                                <div className="vec-dm-bar-fill orange" style={{ width: `${wCusto}%` }} />
                            </div>
                            <span className="vec-dm-bar-value">{formatCurrency(custoOperacional)}</span>
                        </div>

                        <div className="vec-dm-profit-footer">
                            <span className="vec-dm-profit-stat">
                                <FaChartLine /> Lucro líquido:
                                <strong className={balancoColor}>{formatCurrency(lucroTotal)}</strong>
                            </span>
                            <span className="vec-dm-profit-stat">
                                <FaReceipt /> Ticket médio por OS:
                                <strong>{formatCurrency(ticketMedio)}</strong>
                            </span>
                            <span className="vec-dm-profit-stat">
                                <FaTools /> Lançamentos de gasto:
                                <strong>{historico_manutencao.length}</strong>
                            </span>
                        </div>
                    </div>

                    {/* --- LINHA 2: HISTÓRICOS (SPLIT VIEW) --- */}
                    <div className="vec-dm-split-grid">

                        {/* Coluna 1: Ordens de Serviço */}
                        <div className="vec-dm-history-col">
                            <div className="vec-dm-section-header">
                                <h3 className="vec-dm-section-title"><FaListAlt /> Histórico de Serviços</h3>
                                <span className="vec-dm-badge blue">{historico_os.length} registros</span>
                            </div>
                            <div className="vec-dm-history-list">
                                {historico_os.length > 0 ? historico_os.map(os => {
                                    const meta = OS_STATUS_META[(os.status || '').trim()] || { label: os.status || '—', cls: 'gray' };
                                    return (
                                        <div key={`os-${os.id}`} className="vec-dm-h-item">
                                            <div className="vec-dm-h-left">
                                                <strong>
                                                    OS #{os.id}
                                                    <span className={`vec-dm-status ${meta.cls}`}>{meta.label}</span>
                                                </strong>
                                                <span>{os.data_conclusao ? renderDate(os.data_conclusao) : 'Sem data de conclusão'}</span>
                                            </div>
                                            <div className="vec-dm-h-right vec-dm-text-green">
                                                {formatCurrency(os.valor)}
                                            </div>
                                        </div>
                                    );
                                }) : <div className="vec-dm-empty">Nenhum serviço concluído registrado.</div>}
                            </div>
                        </div>

                        {/* Coluna 2: Manutenções */}
                        <div className="vec-dm-history-col">
                            <div className="vec-dm-section-header">
                                <h3 className="vec-dm-section-title"><FaTools /> Histórico de Manutenções</h3>
                                <span className="vec-dm-badge orange">{historico_manutencao.length} registros</span>
                            </div>

                            {insights.ultima_manutencao && (
                                <div className="vec-dm-alert-box">
                                    <strong>Última Manutenção:</strong> {formatDistanceToNow(new Date(insights.ultima_manutencao), { locale: ptBR, addSuffix: true })} ({renderDate(insights.ultima_manutencao)})
                                </div>
                            )}

                            <div className="vec-dm-history-list">
                                {historico_manutencao.length > 0 ? historico_manutencao.map(man => {
                                    const origem = ORIGEM_META[man.origem];
                                    return (
                                        <div key={`man-${man.id}`} className="vec-dm-h-item">
                                            <div className="vec-dm-h-left">
                                                <strong>
                                                    {man.tipo}
                                                    {origem && (
                                                        <span className={`vec-dm-origin ${origem.cls}`}>
                                                            <origem.Icon size={9} /> {origem.label}
                                                        </span>
                                                    )}
                                                </strong>
                                                <span>{renderDate(man.data)}</span>
                                            </div>
                                            <div className="vec-dm-h-right vec-dm-text-orange">
                                                {formatCurrency(man.custo)}
                                            </div>
                                        </div>
                                    );
                                }) : <div className="vec-dm-empty">Nenhuma manutenção registrada.</div>}
                            </div>
                        </div>

                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="vec-dm-overlay" onClick={onClose}>
            <div className="vec-dm-modal" onClick={e => e.stopPropagation()}>
                {renderContent()}
            </div>
        </div>
    );
}